import {matterService} from "../services/matterService";
import {
  contactService,
  bookingsService,
  documentService,
  docuformsService,
  notesService,
  staffService
} from "@/modules/common/services";
import {
  paymentsService,
  paymentsStatusesService,
  estimateService
} from "@/modules/payments/services";
import conversationService from "@/modules/matters/services/conversationService";
import _ from 'lodash'
import Vue from 'vue'

const defaultFilterParams = {
  page: 0,
  per_page: 20
}

const getDefaultState = () =>{
  return {
    matter: null,
    docuform: null,
    loadingMatter: true,
    summary: { loading: true },
    currContact: null,
    contactFields: [],
    activeMatterTab: sessionStorage.getItem('activeMatterTab'),
    bookings: {
      past: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      upcoming: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    },
    conversation: {
      filterBy: {...defaultFilterParams},
      completed: false,
      error: false,
      latest_update: 0,
      messages: [],
    },
    documents:{
      filterBy: {...defaultFilterParams},
      completed: false,
      docs: [],
    },
    docuforms: {
      all: {
        filterBy: {...defaultFilterParams},
        completed: false,
        error: false,
        items: []
      },
      client: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      business: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      internal: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      }
    },
    payments: {
      filterBy: {...defaultFilterParams},
      summary: { loading: false, data: null },
      overdueAndDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      notYetDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      received: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      estimates: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    },
    notes: {
      filterBy: {...defaultFilterParams},
      completed: false,
      notes: [],
    },
    followUp: null,
    bookingSummary: {
      noShows: 0,
      last: null,
      upcoming: null
    },
    collaborators: [],
    collaboratorsPromise: null,
    matterSummaryPromise: null,
    shouldReloadMatter: false
  }
};

const getters = {
  matter: (state) => state.matter,
  reminder: (state) => state.matter && state.matter.reminder,
  summaryLoading: state => state.summary.loading,
  recentNote: (state) => {
    if (!state.summary.recent || !state.summary.recent.note) return

    const note = state.summary.recent.note
    return {
      content: note.content,
      uid: note.uid,
      staffName: note.staff && note.staff.display_name,
      isUpdated: note.created_at !== note.updated_at,
      createdAt: note.created_at,
      updatedAt: note.updated_at_h,
    }
  },
  activeMatterTab: state => state.activeMatterTab,
  pastBookings: (state)  => state.bookings.past.list,
  pastBookingsPage: (state)  => state.bookings.past.filterBy.page,
  pastBookingsCompleted: (state)  => state.bookings.past.completed,
  upcomingBookings: (state) => state.bookings.upcoming.list,
  upcomingBookingsPage: (state)  => state.bookings.upcoming.filterBy.page,
  upcomingBookingsCompleted: (state)  => state.bookings.upcoming.completed,
  currContact: (state) => state.currContact,
  contactFields: state => state.contactFields,
  messages: state => state.conversation.messages,
  messagesPage: (state)  => state.conversation.filterBy.page,
  documents: state => state.documents.docs,
  documentsPage: (state)  => state.documents.filterBy.page,
  paymentsSummary: state  => state.payments.summary.data,
  paymentsSummaryLoading: state  => state.payments.summary.loading,
  overdueAndDuePayments: (state) => {
    return state.payments.overdueAndDue.list
      .map(item => _.pick(item, ['uid', 'title_h', 'amount_to_pay_h', 'state_summary', 'staff', 'payable_type', 'payable_uid']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_to_pay_h,
        status: item.state_summary && item.state_summary.state_h.toLocaleLowerCase(),
        payable_type: item.payable_type,
        payable_uid: item.payable_uid,
        staff: item.staff,
      }))
  },
  overdueAndDuePaymentsPage: (state)  => state.payments.overdueAndDue.filterBy.page,
  overdueAndDuePaymentsCompleted: (state)  => state.payments.overdueAndDue.completed,
  receivedPayments: (state) => {
    return state.payments.received.list
    .map(item => _.pick(item, ['uid', 'title_h', 'amount_h', 'state_summary', 'staff']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_h,
        status: item.state_summary && item.state_summary.state_h.toLocaleLowerCase(),
        staff: item.staff
      }))
  },
  notYetDuePayments: (state) => {
    return state.payments.notYetDue.list
      .map(item => _.pick(item, ['uid', 'title_h', 'amount_to_pay_h', 'state_summary', 'staff', 'payable_type', 'payable_uid']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_to_pay_h,
        status: item.state_summary && item.state_summary.state_h.toLocaleLowerCase(),
        payable_type: item.payable_type,
        payable_uid: item.payable_uid,
        staff: item.staff,
      }))
  },
  notYetDuePaymentsPage: (state)  => state.payments.notYetDue.filterBy.page,
  notYetDuePaymentsCompleted: (state)  => state.payments.notYetDue.completed,
  receivedPaymentsPage: (state)  => state.payments.received.filterBy.page,
  receivedPaymentsCompleted: (state)  => state.payments.received.completed,
  estimatesPayments: (state) => {
    return state.payments.estimates.list
    .map(item => _.pick(item, ['uid', 'title_h', 'amount_h', 'state_h', 'staff']))
      .map(item => ({
        uid: item.uid,
        title: item.title_h,
        price: item.amount_h,
        status: item.state_h.toLocaleLowerCase(),
        staff: item.staff
      }))
  },
  estimatesPaymentsPage: (state)  => state.payments.estimates.filterBy.page,
  estimatesPaymentsCompleted: (state)  => state.payments.estimates.completed,
  notes: state => {
    return state.notes.notes.map(note => {
      return {
        content: note.content,
        uid: note.uid,
        staffUid: note.staff_uid,
        updatedAt: note.updated_at,
        entity: note.entity ? _.pick(note.entity, ['uid', 'title', 'type']) : null,
        page: note.page
      }
    })
  },
  notesPage: (state)  => state.notes.filterBy.page,
  notesCompleted: (state)  => state.notes.completed,
  perPage: () => defaultFilterParams.per_page,
  loadingMatter: (state) => state.loadingMatter,
  bookingSummary: (state) => state.bookingSummary,
  collaborators: state => state.collaborators,
  conversationLatestUpdate: state => state.conversation.latest_update,
  conversationLatestMessageId: state => state.conversation.latest_message_id,
  conversationAllMessagesWasRead: state => state.conversation.all_messages_was_read,
  allDocuforms: state => state.docuforms.all.items,
  allDocuformsCompleted: state => state.docuforms.all.completed,
  allDocuformsError: state => state.docuforms.all.error,
  allDocuformsPage: state => state.docuforms.all.filterBy.page,
  clientDocuforms: state => state.docuforms.client.items,
  clientDocuformsCompleted: state => state.docuforms.client.completed,
  clientDocuformsPage: state  => state.docuforms.client.filterBy.page,
  businessDocuforms: state => state.docuforms.business.items,
  businessDocuformsCompleted: state => state.docuforms.business.completed,
  businessDocuformsPage: state  => state.docuforms.business.filterBy.page,
  internalDocuforms: state => state.docuforms.internal.items,
  internalDocuformsCompleted: state => state.docuforms.internal.completed,
  internalDocuformsPage: state  => state.docuforms.internal.filterBy.page,
  docuform: state  => state.docuform,
  shouldReloadMatter: state => state.shouldReloadMatter
};

const state = getDefaultState()

const mutations = {
  resetState (state) {
    Object.assign(state, getDefaultState())
  },
  setMatter(state, matter) {
    state.matter = matter
  },
  setReminder(state, reminder) {
    Vue.set(state.matter, 'reminder', reminder)
  },
  setCurrContact(state, contact) {
    state.currContact = contact
  },
  setContactFields(state, fields) {
    state.contactFields = fields
  },
  setActiveMatterTab(state, tab) {
    state.activeMatterTab = tab
  },
  setPastBookings(state, bookings) {
    state.bookings.past.list.push(...bookings)
  },
  setPastPage(state, page) {
    state.bookings.past.filterBy.page = page
  },
  setPastBookingsCompleted(state, completed) {
    state.bookings.past.completed = completed
  },
  setUpcomingBookings(state, bookings) {
    state.bookings.upcoming.list.push(...bookings)
  },
  setUpcomingPage(state, page) {
    state.bookings.upcoming.filterBy.page = page
  },
  setUpcomingBookingsCompleted(state, completed) {
    state.bookings.upcoming.completed = completed
  },
  resetBookings(state) {
    state.bookings = {
      past: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      upcoming: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    }
  },

  setMessages(state, messages) {
    state.conversation.messages = _.uniqBy([...state.conversation.messages, ...messages], 'uid');
    state.conversation.latest_update = Math.floor(new Date(state.conversation.messages[0].updated_at)/1000);
  },
  setMessagesCompleted(state, completed) {
    state.conversation.completed = completed
  },
  setMessagesError(state, error) {
    state.conversation.error = error
  },
  setMessagesPage(state, page) {
    state.conversation.filterBy.page = page
  },
  resetConversation(state) {
    state.conversation = {
      filterBy: {...defaultFilterParams},
      completed: false,
      latest_update: 0,
      error: false,
      messages: [],
    }
  },
  setLastUpdateFromTimestamp(state, timestamp) {
    state.conversation.latest_update = parseInt(timestamp)
  },
  updateMessagesWithNewData(state, newData){
    let updatedHash = {}
    newData.reverse().forEach((message)=>{
      let lastMessage = state.conversation.messages[0]
      state.conversation.latest_update = Math.floor(Math.max(new Date(message.updated_at).getTime()/1000, new Date(state.conversation.latest_update).getTime()))

      if (state.conversation.messages.length === 0 || message.created_at > lastMessage.created_at){
        state.conversation.messages.unshift(message)
      } else {
        updatedHash[message.uid] = message
      }
    })

    for (let i = 0; i < state.conversation.messages.length; i++){
      let curUid = state.conversation.messages[i].uid;
      if (updatedHash[curUid]){
        Vue.set(state.conversation.messages, i, updatedHash[curUid])
      }
    }
  },
  addMessage(state, message) {
    state.conversation.messages.unshift(message)
  },

  setDocuments(state, documents) {
    state.documents.docs = documents
  },
  setDocumentsCompleted(state, completed) {
    state.documents.completed = completed
  },
  setDocumentsPage(state, page) {
    state.documents.filterBy.page = page
  },
  resetDocuments(state) {
    state.documents = {
      filterBy: {...defaultFilterParams},
      completed: false,
      docs: [],
    }
  },

  setAllDocuforms(state, docuforms) {
    state.docuforms.all.items.push(...docuforms);
  },
  setAllDocuformsPage(state, page) {
    state.docuforms.all.filterBy.page = page;
  },
  setAllDocuformsCompleted(state, completed) {
    state.docuforms.all.completed = completed;
  },
  setAllDocuformsError(state, error) {
    state.docuforms.all.error = error;
  },

  setClientDocuforms(state, docuforms) {
    state.docuforms.client.items.push(...docuforms)
  },
  setClientDocuformsPage(state, page) {
    state.docuforms.client.filterBy.page = page
  },
  setClientDocuformsCompleted(state, completed) {
    state.docuforms.client.completed = completed
  },

  setBusinessDocuforms(state, docuforms) {
    state.docuforms.business.items.push(...docuforms)
  },
  setBusinessDocuformsPage(state, page) {
    state.docuforms.business.filterBy.page = page
  },
  setBusinessDocuformsCompleted(state, completed) {
    state.docuforms.business.completed = completed
  },

  setInternalDocuforms(state, docuforms) {
    state.docuforms.internal.items.push(...docuforms)
  },
  setInternalDocuformsPage(state, page) {
    state.docuforms.internal.filterBy.page = page
  },
  setInternalDocuformsCompleted(state, completed) {
    state.docuforms.internal.completed = completed
  },

  resetDocuforms(state) {
    state.docuforms = {
      all: {
        filterBy: {...defaultFilterParams},
        completed: false,
        error: false,
        items: []
      },
      client: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      business: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      },
      internal: {
        filterBy: {...defaultFilterParams},
        completed: false,
        items: []
      }
    };
  },

  setDocuform(state, item) {
    state.docuform = item
  },

  setPaymentsSummaryData(state, summary) {
    Vue.set(state.payments.summary, 'data', summary)
  },

  setPaymentsSummaryLoading(state, isLoading) {
    Vue.set(state.payments.summary, 'loading', isLoading)
  },

  setOverdueAndDuePaymentsLoader(state, isLoading) {
    Vue.set(state.payments.overdueAndDue, 'loading', isLoading)
  },
  setOverdueAndDuePayments(state, payments) {

    state.payments.overdueAndDue.list.push(...payments)
  },
  setOverdueAndDuePaymentsPage(state, page) {
    state.payments.overdueAndDue.filterBy.page = page
  },
  setOverdueAndDuePaymentsCompleted(state, completed) {
    state.payments.overdueAndDue.completed = completed
  },

  setNotYetDuePaymentsLoader(state, isLoading) {
    Vue.set(state.payments.notYetDue, 'loading', isLoading)
  },
  setNotYetDuePayments(state, payments) {

    state.payments.notYetDue.list.push(...payments)
  },
  setNotYetDuePaymentsPage(state, page) {
    state.payments.notYetDue.filterBy.page = page
  },
  setNotYetDuePaymentsCompleted(state, completed) {
    state.payments.notYetDue.completed = completed
  },

  setReceivedPayments(state, payments) {
    state.payments.received.list.push(...payments)
  },
  setReceivedPaymentsPage(state, page) {
    state.payments.received.filterBy.page = page
  },
  setReceivedPaymentsCompleted(state, completed) {
    state.payments.received.completed = completed
  },

  setEstimatesPayments(state, payments) {
    state.payments.estimates.list.push(...payments)
  },
  setEstimatesPaymentsPage(state, page) {
    state.payments.estimates.filterBy.page = page
  },
  setEstimatesPaymentsCompleted(state, completed) {
    state.payments.estimates.completed = completed
  },

  resetPayments(state) {
    state.payments = {
      filterBy: {...defaultFilterParams},
      summary: { loading: false, data: null},
      overdueAndDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      notYetDue: {
        filterBy: {...defaultFilterParams},
        completed: false,
        loading: false,
        list: [],
      },
      received: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      },
      estimates: {
        filterBy: {...defaultFilterParams},
        completed: false,
        list: [],
      }
    }
  },

  setNotes(state, notes) {
    state.notes.notes.push(...notes)
  },
  setNotesCompleted(state, completed) {
    state.notes.completed = completed
  },
  setNotesPage(state, page) {
    state.notes.filterBy.page = page
  },

  resetNotes(state) {
    state.notes = {
      filterBy: {...defaultFilterParams},
      completed: false,
      notes: [],
    }
  },

  removeNote(state, noteId) {
    state.notes.notes = state.notes.notes.filter(n => n.id !== noteId)
  },

  setMatterSummaryLoading(state, isLoading) {
    Vue.set(state.summary, 'loading', isLoading)
  },
  setRecentSummary(state, recent) {
    Vue.set(state.summary, 'recent', recent)
  },
  setLoadingMatter(state, loading) {
    state.loadingMatter = loading;
  },
  setBookingSummary(state, bookingSummary) {
    state.bookingSummary = bookingSummary;
  },
  setCollaborators(state, collaborators) {
    state.collaborators = collaborators;
  },
  setCollaboratorsPromise(state, promise) {
    state.collaboratorsPromise = promise;
  },
  setMatterSummaryPromise(state, promise) {
    state.matterSummaryPromise = promise;
  },
  setMatterReload(state, reload) {
    state.shouldReloadMatter = reload;
  }
}

const actions = {
  async fetchMatterData({dispatch}, {payload}) {
    const success = await dispatch({type: "fetchMatter", payload});
    success && await dispatch({type: "fetchContact"});
  },
  async fetchMatter({commit, dispatch, state}, {payload}) {
    const {matterUid, reload} = payload

    try {
      if (reload || !state.matter) {
        commit('setLoadingMatter', true)
        commit('resetBookings')
        commit('resetConversation')
        commit('resetPayments')
        commit('resetDocuments')
        commit('resetDocuforms')
        commit('resetNotes')
        commit('setMatter', null)
      }

      const res = await matterService.get(matterUid)
      commit('setMatter', res.data.matter)
      commit('setLoadingMatter', false)
      commit('setMatterReload', false);
      return res.data
    } catch(e) {
      console.error('error', error);
      commit('setLoadingMatter', false)
    }
  },
  async fetchContact({commit, dispatch, state, rootState}) {
    const {uid} = state.matter.contacts[0];
    const {error, client} = await contactService.getClient(uid);

    if (error) {
      console.error(error);
      commit('setCurrContact', null);
      return;
    }

    if (rootState.clientFields && !rootState.clientFields.clientFields.length) {
      await dispatch('clientFields/fetchClientFields', null, {root: true});
    }

    const {data} = await contactService.getContact(uid);

    if (data && data.contact && rootState.clientFields && rootState.clientFields.clientFields.length) {
      commit('setContactFields', data.contact.fields);

      const {clientFields} = rootState.clientFields;
      client.custom_fields = {};

      data.contact.fields.forEach(field => {
        if (field.value === null) {
          return;
        }

        if (!field.uid) {
          if (clientFields.find(clientField => clientField.label === field.label)) {
            return;
          } else {
            client.custom_fields[field.label] = field.value;
            return;
          }
        }

        const fieldSettings = clientFields.find(clientField => clientField.id === field.uid);

        if (fieldSettings && fieldSettings.custom && fieldSettings.type !== 'phone') {
          client.custom_fields[field.label] = field.value;
        }
      });
    }

    commit('setCurrContact', client);
  },
  setLastUpdate({commit}, {payload}) {
    const {timestamp} = payload;
    commit('setLastUpdateFromTimestamp', timestamp);
  },
  saveMatterTab({commit}, {payload}) {
    const {tab} = payload;
    sessionStorage.setItem('activeMatterTab', tab);
    commit('setActiveMatterTab', tab);
  },
  async loadPastBookings({commit, state}, {payload}) {
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.bookings.past.filterBy,
      client: payload.clientId,
      conversation_id: matterUid,
      end_date: new Date(Date.now()).toISOString()
    }
    const {error, bookings} = await bookingsService.list(filterBy)
    if (error) {
      console.error('error', error);
      return
    }

    if (!bookings || bookings.length === 0) {
      commit('setPastBookingsCompleted', true)
      return
    }

    commit('setPastBookings', bookings)
    if (bookings.length < state.bookings.past.filterBy.per_page) {
      commit('setPastBookingsCompleted', true)
    }
  },
  async loadUpcomingBookings({commit, state}, {payload}) {
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.bookings.upcoming.filterBy,
      page: payload.page || state.bookings.upcoming.filterBy.page,
      client: payload.clientId,
      conversation_id: matterUid,
      start_date: new Date(Date.now()).toISOString(),
      sort_order: 'asc'
    }

    const {error, bookings} = await bookingsService.list(filterBy)
    if (error) {
      console.error('error', error);
      return
    }

    if (!bookings || bookings.length === 0) {
      commit('setUpcomingBookingsCompleted', true)
      return
    }

    commit('setUpcomingBookings', bookings)
    if (bookings.length < state.bookings.upcoming.filterBy.per_page) {
      commit('setUpcomingBookingsCompleted', true)
    }
  },

  resetBookings({commit}) {
    commit('resetBookings')
  },
  resetState ({commit}) {
    commit('resetState')
  },
  async loadMessages({commit, state}) {
    const params = {
      ...state.conversation.filterBy,
      localized: true,
      full: true
    }

    try {
      const res = await conversationService.getMessages(state.matter.uid, params)
      const messages = res.data
      if (!messages || messages.length === 0) {
        commit('setMessagesCompleted', true)
        return
      }
      commit('setMessages', messages)
    }
    catch (error) {
      console.error('error', error);
      commit('setMessagesError', true)
    }
  },

  async sendMessage({commit, state}, {payload}) {
    const {error, message} = await conversationService.sendMessage(state.matter.uid, payload.message)

    if (error) {
      console.error('error', error);
      return
    }

    commit('addMessage', message)
  },

  async updateConversation({state}, {payload}) {
    try {
      if (state.matter && state.matter.uid && payload)
        await conversationService.update(state.matter.uid, payload.data)
    } catch(e) {
      console.error('error:', e.error)
    }
  },

  resetConversation({commit}) {
    commit('resetConversation')
  },
  async fetchNewMessageUpdates({commit}, {payload}) {
    let filter = state.conversation.filterBy

    const params = {
      ...filter,
      last_update: new Date(),
      localized: !payload.getAll,
      full: true
    }

    params.page = 0

    const res = await conversationService.getMessages(state.matter.uid, params)

    if (res.data && res.data.length > 0){
      commit('updateMessagesWithNewData', res.data)
    }
  },
  async loadDocuments({commit, state}, {payload}) {
    const filterBy = {
      ...state.documents.filterBy,
      client: payload.clientId,
      conversation_uid: payload.matterId,
    }
    const {error, documents} = await documentService.list(filterBy)
    if (error) {
      console.error('error', error);
      return
    }

    if (!documents || documents.length === 0) {
      commit('setDocumentsCompleted', true)
      return
    }

    commit('setDocuments', _.uniqBy([...state.documents.docs, ...documents], 'uid'))
    if (documents.length < state.documents.filterBy.per_page) {
      commit('setDocumentsCompleted', true)
    }
  },

  resetDocuments({commit}) {
    commit('resetDocuments')
  },

  async loadDocuforms({commit, state, rootState}, {payload}) {
    const listName = payload.initiator.toLowerCase();
    const capListName = _.capitalize(listName);
    let statusFilter = '';

    switch (payload.initiator) {
      case "client":
        statusFilter = 'Submitted,Approved,Rejected';
        break;
      case "business":
        statusFilter = 'Reviewed,Pending';
        break;
      case "internal":
        statusFilter = 'Internal';
        break;
    }

    const params = {
      ...state.docuforms[listName].filterBy,
      'filter[matter_uid]': state.matter.uid,
      'filter[status][in]': statusFilter
    }

    const {errors, docuforms} = await docuformsService.getFilteredList(params);

    if (errors) {
      console.error('error', errors[0].message);
      return;
    }

    docuforms.forEach(docuform => {
      const staff = rootState.accountModule.staffMembers.find(staff => staff.uid === docuform.staff_uid);

      if (staff) {
        docuform.staff_name = staff.full_name;
      }
    });

    if (!docuforms || docuforms.length === 0) {
      commit(`set${capListName}DocuformsCompleted`, true);
      return;
    }

    commit(`set${capListName}Docuforms`, docuforms);

    if (docuforms.length < state.docuforms[listName].filterBy.per_page) {
      commit(`set${capListName}DocuformsCompleted`, true);
    }
  },

  async loadAllDocuforms({commit, state, rootState}) {
    const {error, docuforms} = await docuformsService.getList(state.docuforms.all.filterBy);

    if (error) {
      console.error('error', error);
      commit('setAllDocuformsError', true);
      return;
    }

    commit('setAllDocuformsError', false);

    if (!docuforms || docuforms.length === 0) {
      commit('setAllDocuformsCompleted', true);
      return;
    }

    const matterUids = _.uniq(docuforms.map(docuform => docuform.matter_uid));
    const res = await Promise.all([matterService.getMultiple(matterUids), matterService.getAvatars(matterUids)]);
    const matters = res[0];
    const avatars = res[1];

    docuforms.forEach(docuform => {
      const staff = rootState.accountModule.staffMembers.find(staff => staff.uid === docuform.staff_uid);
      const matter = matters.data.matters.find(matter => matter.uid === docuform.matter_uid);

      if (staff) {
        docuform.staff_name = staff.full_name;
      }

      if (matter) {
        docuform.matter = matter;
        docuform.contact = matter.contacts[0];
        docuform.contact.full_name = docuform.contact.display_name;

        const avatar = avatars.data.avatars.find(avatar => avatar.matter_uid === docuform.matter.uid);

        if (avatar) {
          docuform.contact.photo_path = avatar.photo;
          docuform.contact.color_id = avatar.color;
        }
      }
    });

    commit('setAllDocuforms', docuforms);

    if (docuforms.length < state.docuforms.all.filterBy.per_page) {
      commit('setAllDocuformsCompleted', true);
    }
  },

  resetDocuforms({commit}) {
    commit('resetDocuforms')
  },

  async loadDocuform({commit, state, rootState}, {payload}) {
    const docuform = state.docuforms.all.items.find(doc => doc.uid === payload.uid);

    if (docuform) {
      commit('setDocuform', docuform);
      return;
    }

    const item = await docuformsService.getActivity(payload.uid);
    const staff = rootState.accountModule.staffMembers.find(staff => staff.uid === item.staff_uid);

    if (staff) {
      item.staff_name = staff.full_name;
    }

    return commit('setDocuform', item);
  },

  async loadPaymentsSummary({commit, state}) {
    // todo: use as promise and prevent double call for the server
    const clientUid = state.matter.contacts[0].uid
    const filterBy = {id: clientUid, conversation_id: state.matter.uid, exclude_items:true}

    if (state.payments.summary.loading) return

    commit('setPaymentsSummaryLoading', true)
    try {
      const res = await paymentsService.getSummary(filterBy);
      commit('setPaymentsSummaryData', res.data.balances)
    } catch(e) {
      console.error('error', e);
    }
    commit('setPaymentsSummaryLoading', false)
  },

  async loadOverdueAndDuePayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.overdueAndDue.filterBy,
      client: clientUid,

      conversation_id: matterUid,
      by_status: "past_due,outstanding",
      sort_by: 'created_at',
      has_invoice: 'false',
      full: true,
    }

    try {
      commit('setOverdueAndDuePaymentsLoader', true)
      const res = await paymentsStatusesService.list(filterBy)
      const data = (res.data || []).sort((a,b) => (a.state > b.state) ? 1 : ((b.state > a.state) ? -1 : 0));
      commit('setOverdueAndDuePayments', data)
      if (data.length < state.payments.overdueAndDue.filterBy.per_page) {
        commit('setOverdueAndDuePaymentsCompleted', true)
      }
    } catch(e) {
      console.error('error', e);
    }

    commit('setOverdueAndDuePaymentsLoader', false)
  },

  async loadNotYetDuePayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.notYetDue.filterBy,
      client: clientUid,

      conversation_id: matterUid,
      by_status: "not_yet_due",
      sort_by: 'created_at',
      has_invoice: 'false',
      full: true,
    }

    try {
      commit('setNotYetDuePaymentsLoader', true)
      const res = await paymentsStatusesService.list(filterBy)
      const data = res.data || []
      commit('setNotYetDuePayments', data)
      if (data.length < state.payments.notYetDue.filterBy.per_page) {
        commit('setNotYetDuePaymentsCompleted', true)
      }
    } catch(e) {
      console.error('error', e);
    }

    commit('setNotYetDuePaymentsLoader', false)
  },

  async loadReceivedPayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.received.filterBy,
      client: clientUid,
      conversation_id: matterUid,
      by_payment_method: 'all',
      by_status: 'paid',
      sort_by: 'paid_at',
      full: true,
      localized: true,
    }

    try {
      const res = await paymentsService.list(filterBy);
      const data = res.data || []
      commit('setReceivedPayments', data)
      if (data.length < state.payments.received.filterBy.per_page) {
        commit('setReceivedPaymentsCompleted', true)
      }
    } catch(e) {
      console.error('error', e);
    }
  },

  async loadEstimatesPayments({commit, state}) {
    const clientUid = state.matter.contacts[0].uid
    const matterUid = state.matter.uid
    const filterBy = {
      ...state.payments.estimates.filterBy,
      client: clientUid,
      conversation_id: matterUid,
      has_invoice: false,
      by_payment_method: 'all',
      by_status: 'issued,draft,approved,invoiced',
      sort_by: 'created_at',
      full: true,
    }

    try {
      const res = await estimateService.list(filterBy)
      const data = res.data || []
      commit('setEstimatesPayments', data)
      if (data.length < state.payments.estimates.filterBy.per_page) {
        commit('setEstimatesPaymentsCompleted', true)
      }
    } catch (e) {
      console.error('error', e);
    }

  },

  resetPayments({commit}) {
    commit('resetPayments')
  },

  async loadNotes({commit, state}, {payload}) {
    const filterBy = {
      ...state.notes.filterBy
    }

    const {errors, data} = await notesService.list(payload.matterId, filterBy)

    if (errors) {
      console.error(errors[0].message);
      return;
    }

    const notes = data.notes

    if (!notes || notes.length === 0) {
      commit('setNotesCompleted', true)
      return
    }

    notes.forEach(note => note.page = state.notes.filterBy.page)

    commit('setNotes', notes)
    if (notes.length < state.notes.filterBy.per_page) {
      commit('setNotesCompleted', true)
    }
  },

  resetNotes({commit}) {
    commit('resetNotes')
  },

  async removeNote({commit, state}, {payload}) {
    const noteId = payload.id
    const {error} = await notesService.remove(state.matter.uid, noteId)

    if (error) {
      console.error(error);
      return;
    }

    commit('removeNote', noteId)
  },

  async loadMatterSummary({commit, state}, payload) {
    if ((!payload || !payload.refresh) && state.matterSummaryPromise) {
      return state.matterSummaryPromise;
    }

    commit('setMatterSummaryLoading', true);

    const promise = matterService.summary(state.matter.uid).then(res => {
      if (res.data.summary) {
        commit('setRecentSummary', res.data.summary.recent);
        commit('setBookingSummary', res.data.summary.booking);
      }
    }).catch(error => {
      console.error("loadMatterSummary", error);
    }).finally(() => {
      commit('setMatterSummaryLoading', false);
    });

    commit('setMatterSummaryPromise', promise);
    return promise;
  },

  async refreshMatterSummary({dispatch}) {
    await dispatch({type: "loadMatterSummary", refresh: true});
  },

  async fetchCollaborators({commit, state}, payload) {
    if ((!payload || !payload.refresh) && state.collaboratorsPromise) {
      return state.collaboratorsPromise;
    }

    const promise = staffService.getCollaborators(state.matter.uid).then(collaborators => {
      commit('setCollaborators', collaborators);
    }, error => {
      console.error("fetchCollaborators", error);
    });

    commit('setCollaboratorsPromise', promise);
    return promise;
  },

  async refreshCollaborators({dispatch}) {
    await dispatch({type: "fetchCollaborators", refresh: true});
  },

  setMatterReload({commit}) {
    commit('setMatterReload', true);
  }
}


export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
